import { authenticatedFetch } from './auth';

export const SERVICE_NAMES = [
  'bluetooth.service',
  'ble-server-levo.service',
  'mcbapp.service',
  'system-controller.service',
  'lm-dbus-session.service',
  'update.service',
] as const;

export type ServiceName = (typeof SERVICE_NAMES)[number];
export type ServiceAction = 'start' | 'stop' | 'restart';

export interface BluetoothPeer {
  address: string;
  name?: string;
}

export interface BluetoothStatus {
  available: boolean;
  powered: boolean;
  advertising: boolean;
  discoverable: boolean;
  pairable: boolean;
  address?: string | undefined;
  name?: string | undefined;
  connectedPeerCount: number;
  connectedPeers: BluetoothPeer[];
  managerProcess?: { pid: number; name?: string; executable?: string; args?: string[] } | undefined;
  applicationProcess?: { pid: number; name?: string; command?: string; cwd?: string } | undefined;
  error?: string | undefined;
}

export interface ServiceStatus {
  name: string;
  description?: string | undefined;
  loadState: string;
  activeState: string;
  subState: string;
  unitFileState?: string | undefined;
  canStart?: boolean | undefined;
  canStop?: boolean | undefined;
  controllable: boolean;
}

export interface OperationalStatus {
  collectedAt: string;
  bluetooth: BluetoothStatus;
  services: ServiceStatus[];
}

export interface DeviceCapabilities {
  operationalTelemetry?: boolean;
  serviceControl?: boolean;
  canMonitor?: boolean;
  canStreaming?: boolean;
  sshKeyInstall?: boolean;
  serviceLogs?: boolean;
}

export function appendServiceLogLines(current: string[], incoming: string[], limit = 2_000): string[] {
  if (limit <= 0) return [];
  const combined = [...current, ...incoming];
  return combined.length <= limit ? combined : combined.slice(combined.length - limit);
}

export interface ExtendedDeviceStatus {
  capabilities?: DeviceCapabilities;
  operational?: OperationalStatus;
  bluetooth?: Omit<BluetoothStatus, 'connectedPeers' | 'connectedPeerCount'> & {
    connectedDevices?: BluetoothPeer[];
  };
  services?: ServiceStatus[];
  operationalCollectedAt?: string;
}

export function readOperationalStatus(status: ExtendedDeviceStatus): OperationalStatus | undefined {
  if (status.operational) return status.operational;
  if (!status.bluetooth && !status.services && !status.operationalCollectedAt) return undefined;
  const peers = status.bluetooth?.connectedDevices ?? [];
  return {
    collectedAt: status.operationalCollectedAt ?? '',
    bluetooth: {
      available: status.bluetooth?.available ?? false,
      powered: status.bluetooth?.powered ?? false,
      advertising: status.bluetooth?.advertising ?? false,
      discoverable: status.bluetooth?.discoverable ?? false,
      pairable: status.bluetooth?.pairable ?? false,
      address: status.bluetooth?.address,
      name: status.bluetooth?.name,
      connectedPeers: peers,
      connectedPeerCount: peers.length,
      managerProcess: status.bluetooth?.managerProcess,
      applicationProcess: status.bluetooth?.applicationProcess,
      error: status.bluetooth?.error,
    },
    services: status.services ?? [],
  };
}

export interface CanFrame {
  sequence?: number | undefined;
  timestamp?: string | undefined;
  interface?: string | undefined;
  id: string;
  data: string;
  raw?: string | undefined;
  flags?: string[] | undefined;
}

export interface CanFilters {
  id: string;
  payload: string;
}

export function appendCanFrames(current: CanFrame[], incoming: CanFrame[], limit = 2_000): CanFrame[] {
  if (limit <= 0) return [];
  const combined = [...current, ...incoming];
  return combined.length <= limit ? combined : combined.slice(combined.length - limit);
}

export function filterCanFrames(frames: CanFrame[], filters: CanFilters): CanFrame[] {
  const id = filters.id.trim().toLowerCase().replace(/^0x/, '');
  const payload = filters.payload.trim().toLowerCase().replace(/\s+/g, '');
  return frames.filter((frame) => {
    const frameId = frame.id.toLowerCase().replace(/^0x/, '');
    const framePayload = frame.data.toLowerCase().replace(/\s+/g, '');
    return (!id || frameId.includes(id)) && (!payload || framePayload.includes(payload));
  });
}

export function normalizeCanFrame(value: unknown): CanFrame | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const id = candidate.id ?? candidate.canId;
  const data = candidate.data ?? candidate.payload;
  if ((typeof id !== 'string' && typeof id !== 'number') || typeof data !== 'string') return null;
  return {
    sequence: typeof candidate.sequence === 'number' ? candidate.sequence : undefined,
    timestamp: normalizeCanTimestamp(candidate.timestamp),
    interface: typeof candidate.interface === 'string' ? candidate.interface : undefined,
    id: String(id),
    data,
    raw: typeof candidate.raw === 'string' ? candidate.raw : undefined,
    flags: Array.isArray(candidate.flags)
      ? candidate.flags.filter((flag): flag is string => typeof flag === 'string')
      : undefined,
  };
}

export function normalizeCanTimestamp(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const numeric = Number(value);
  const milliseconds = Number.isFinite(numeric) ? numeric * 1_000 : Date.parse(value);
  if (!Number.isFinite(milliseconds)) return undefined;
  return new Date(milliseconds).toISOString();
}

function csvCell(value: string | number | undefined): string {
  const text = value === undefined ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function canFramesToCsv(frames: CanFrame[]): string {
  const rows = frames.map((frame) =>
    [frame.timestamp, frame.interface, frame.id, frame.data, frame.raw].map(csvCell).join(','),
  );
  return ['timestamp,interface,id,data,raw', ...rows].join('\r\n');
}

export function parseOpenSshPublicKey(value: string): {
  normalized: string;
  type: string;
  bytes: Uint8Array;
} {
  const normalized = value.trim().replace(/\r?\n/g, ' ');
  const parts = normalized.split(/\s+/);
  if (parts.length < 2 || !/^(ssh-|ecdsa-|sk-)/.test(parts[0] ?? '')) {
    throw new Error('Enter a valid OpenSSH public key.');
  }
  let raw: string;
  try {
    raw = atob(parts[1] ?? '');
  } catch {
    throw new Error('The public key body is not valid base64.');
  }
  if (!raw) throw new Error('The public key body is empty.');
  const bytes = Uint8Array.from(raw, (character) => character.charCodeAt(0));
  return { normalized, type: parts[0] ?? '', bytes };
}

export async function sshFingerprint(value: string): Promise<string> {
  const { bytes } = parseOpenSshPublicKey(value);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(bytes).buffer));
  let binary = '';
  for (const byte of digest) binary += String.fromCharCode(byte);
  return `SHA256:${btoa(binary).replace(/=+$/, '')}`;
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const response = await authenticatedFetch(url, init);
  const result = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!response.ok) throw new Error(result?.error ?? `${response.status} ${response.statusText}`);
  return (result ?? ({} as T)) as T;
}

export function refreshOperationalStatus(deviceId: string): Promise<unknown> {
  return postJson(`/api/devices/${encodeURIComponent(deviceId)}/refresh`);
}

export function controlService(deviceId: string, service: string, action: ServiceAction): Promise<unknown> {
  return postJson(
    `/api/devices/${encodeURIComponent(deviceId)}/services/${encodeURIComponent(service)}/${action}`,
  );
}

export function installDeveloperSshKey(deviceId: string, publicKey: string): Promise<unknown> {
  return postJson(`/api/devices/${encodeURIComponent(deviceId)}/ssh-keys`, { publicKey });
}
